import React, { Fragment, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Select, { components } from "react-select";
import { Button, Row, Col, Card, CardBody, Container, FormGroup, Label, Input, ButtonDropdown, DropdownToggle, DropdownMenu, DropdownItem } from "reactstrap";
import moment from "moment";
import { Link } from "react-router-dom";
import { PopoverBody } from 'reactstrap';
import {useHistory, useParams} from "react-router-dom";

import PopoverFormula from './components/PopoverFormula';
import BootstrapTable from "../../../share-components/table/BootstrapTable";
import Breadcrumb from "../../../layout/breadcrumb";

import withReducer from "../../../store/withReducer";
import reportReducer from "../index";

import {
    getXeroInvoiceSetting,
    getReportXeroInvoices,
    exportInvoices,
    exportInvoiceItems,
    exportXeroInvoiceDetailReport,
} from "./store/xeroInvoiceReportSlice";

import {
    formatterCurrency
} from "../../../utils/common";
import DateRangePickerDefault from "../../../share-components/ui/DateRangePickerDefault";


const XeroInvoice = (props) => {

    const xeroInvoiceState = useSelector(
        ({ report }) => report.xeroInvoiceReport
    );

    const dispatch = useDispatch();
    const queryParams = new URLSearchParams(window.location.search);

    // helper to ensure a value is a valid date/moment/string parseable by moment
    const isValidDate = (d) => d !== null && d !== undefined && moment(d).isValid();

    // parse query params and normalize invalid dates to undefined
    const _rawEnd = queryParams.has('end_date') ? new Date(queryParams.get('end_date')) : undefined;
    const _rawStart = queryParams.has('start_date') ? new Date(queryParams.get('start_date')) : undefined;
    const endDateObj = isValidDate(_rawEnd) ? _rawEnd : undefined;
    const startDateObj = isValidDate(_rawStart) ? _rawStart : undefined;

    // initialize dateRange with validated dates (undefined when absent/invalid)
    const [dateRange, setDateRange] = useState([startDateObj, endDateObj]);
    const [startDate, endDate] = dateRange;

    // safe disabledDate: return false for falsy/invalid input
    const afterToday = () => {
        return (date) => {
            if (!isValidDate(date)) return false;
            return date.getTime() > Date.now();
        };
    };

    const [timer, setTimer] = useState(null);
    const [exportDropdownOpen, setExportDropdownOpen] = useState(false);
    const exportToggle = () => setExportDropdownOpen((prevState) => !prevState);
    const [exportBtnDisabled, setExportBtnDisabled] = useState(false);
    const [loading, setLoading] = useState(true);
    const [exportLoadingMap, setExportLoadingMap] = useState({});

    const [filterInvoiceNumber, setFilterInvoiceNumber] = useState('');
    const [filterContact, setFilterContact] = useState('');
    const [filterCurrency, setFilterCurrency] = useState(null);
    const [filterType, setFilterType] = useState(null);
    const [filterSourceType, setFilterSourceType] = useState(null);
    const [filterXeroStatus, setFilterXeroStatus] = useState(null);

    const [lastFillters, setLastFillters] = useState(null);
    const [filters, setFilters] = useState({
        page: 1,
        pageSize: 10,
        // set initial dates to null if not provided
        start_date: isValidDate(startDate) ? moment(startDate).format("YYYY-MM-DD") : null,
        end_date: isValidDate(endDate) ? moment(endDate).format("YYYY-MM-DD") : null,
        // Add new filter properties
        contact_detail: filterContact,
        type: filterType,
        source_type: filterSourceType,
        xero_id: null,
        invoice_number: filterInvoiceNumber,
        currency: filterCurrency,
        xero_status: filterXeroStatus,
        error_message: '',
        amount_discrepancy: ''
    });

    const dataMenu = [
        {
            type: 0,
            text: "Reports",
        },
        {
            type: 0,
            text: "Xero Invoices",
        },
    ];
    
    useEffect(() => {
        dispatch(getXeroInvoiceSetting());
    }, [dispatch]);

    const getReport = () => {
        if (JSON.stringify(filters) == JSON.stringify(lastFillters)) return;

        setLastFillters({ ...filters });
        setLoading(true);

        dispatch(
            getReportXeroInvoices(filters)
        ).then((res) => {
            if (!res.error) {
                setLoading(false);
            }
        });
    };

    useEffect(() => {
        getReport();
    }, [dispatch, filters]);

    const onSendToXero = (row) => {
        // dispatch(sendShopifyPayoutToXero({
        //     payment_id: row.payment_id,
        //     invoice_id: row.invoice_id,
        //     invoice_id: row.invoice_id,
        //     invoice_id: row.invoice_id,
        // })).then((res) => {
        //     if (!res.error) {
        //         getReport();
        //     }
        // });
    }
    
    const exportRow = async (row) => {
        if (!row || exportLoadingMap[row.invoice_id]) return;
        setExportLoadingMap(prev => ({ ...prev, [row.invoice_id]: true }));
        try {
            const params = { invoice_id: row.invoice_id }
            dispatch(exportXeroInvoiceDetailReport(params)).then((res) => {
                if (res.payload === undefined) return;
                const link = document.createElement("a");
                link.href = res.payload;
                link.setAttribute("download", "");
                document.body.appendChild(link);
                link.click();
                link.remove();
            });
        } catch (err) {
            // handle error if needed
            console.error("Export failed for invoice", row.invoice_id, err);
        } finally {
            // keep button disabled briefly to show spinner; remove after short delay
            setTimeout(() => {
                setExportLoadingMap(prev => {
                    const copy = { ...prev };
                    delete copy[row.invoice_id];
                    return copy;
                });
            }, 1500);
        }
    };

    const tableColumns = [
        {
            name: 'Invoice Date',
            selector: row => {
                const s = row.start_date ? moment(row.start_date) : null;
                const e = row.end_date ? moment(row.end_date) : null;

                if (s && e) {
                    if (s.isSame(e, 'day')) {
                        return e.format("MMM DD, YYYY");
                    }
                    return `${s.format("MMM DD, YYYY")} - ${e.format("MMM DD, YYYY")}`;
                }

                if (e) return e.format("MMM DD, YYYY");
                if (s) return s.format("MMM DD, YYYY");
                return '-';
            },
            left: true,
        },
        {
            name: 'Invoice Number',
            selector: row => row.invoice_number || '-',
            left: true,
        },
        {
            name: 'Invoice ID',
            selector: row => row.invoice_id ?? '-',
            left: true,
        },
        {
            name: 'Contact Name',
            selector: row => row.contact_name || '-',
            left: true,
        },
        {
            name: 'Type',
            selector: row => row.type || '-',
            left: true,
        },
        {
            name: 'Source Type',
            selector: row => row.source_type || '-',
            left: true,
        },
        {
            name: 'Reference',
            selector: row => row.reference || '-',
            left: true,
        },
        {
            name: 'Currency',
            selector: row => row.currency || '-',
            left: true,
        },
        {
            name: 'Total Gross Amount',
            selector: row => formatterCurrency(row.xero_total_gross_amount || 0),
            right: true,
        },
        {
            name: 'Total Tax',
            selector: row => formatterCurrency(row.xero_total_tax_amount || 0),
            right: true,
        },
        {
            name: 'Total Amount',
            selector: row => formatterCurrency(row.xero_total_net_amount || 0),
            right: true,
        },
        {
            name: 'Xero Status',
            selector: row => {
                return <>
                    {row.xero_status || '-'}
                    {row.error_message && row.error_message != '' && (
                    <PopoverFormula className="ml-2" id={'errorMessage_' + row.invoice_id}>
                        <PopoverBody>
                            <p>{row.error_message}</p>
                        </PopoverBody>
                    </PopoverFormula>
                    )}
                </>
            },
            center: true,
        },
        {
            name: 'Actions',
            selector: row => {
                const isLoading = !!exportLoadingMap[row.invoice_id];
                return (
                    <div className="text-right">
                        <Button
                            size="xs"
                            color="primary"
                            disabled={isLoading}
                            onClick={() => exportRow(row)}
                        >
                            {isLoading ? <i className="fa fa-spinner fa-spin mr-1"></i> : <i className="fa fa-download mr-1"></i>}
                            Export
                        </Button>
                    </div>
                );
            },
            right: true,
            grow: 0.4,
        },
    ];

    const onPageSizeChange = (newPageSize) => {
        setFilters(prev => ({
            ...prev,
            page: 1,
            pageSize: newPageSize === 'All' ? xeroInvoiceState.xeroInvoicesTableRow : newPageSize
        }));
    }

    const onPageChange = (e) => {
        setFilters({ ...filters, page: e })
    }

    useEffect(() => {
        setFilters(prev => ({
            ...prev,
            start_date: isValidDate(startDate) ? moment(startDate).format("YYYY-MM-DD") : null,
            end_date: isValidDate(endDate) ? moment(endDate).format("YYYY-MM-DD") : null,
        }));
    }, [startDate, endDate]);


    // Handle individual filters changes
    const handleFilterChange = (key, value) => {
        setFilters(prev => ({
            ...prev,
            [key]: value,
            page: 1, // Reset to first page when filters change
        }));
    };

    const handleTextInputChange = (key, value) => {
        clearTimeout(timer);
        const newTimer = setTimeout(() => {
            setFilters(prev => ({
                ...prev,
                [key]: value,
                page: 1, // Reset to first page when filters change
            }));
        }, 500);
        setTimer(newTimer)
    };

    // Clear all filters
    const clearFilters = () => {
        setFilters(prev => ({
            ...prev,
            contact_detail: '',
            type: null,
            source_type: null,
            xero_id: null,
            invoice_number: '',
            currency: null,
            xero_status: null,
            error_message: '',
            amount_discrepancy: '',
            page: 1 // Reset to first page when filters change
        }));
        setFilterInvoiceNumber('');
        setFilterContact('');
        setFilterCurrency(null);
        setFilterType(null);
        setFilterSourceType(null);
        setFilterXeroStatus(null)
    };
    
    const exportInvoicesAction = () => {
        if (exportBtnDisabled) return;
        setExportBtnDisabled(true);

        dispatch(
            exportInvoices(filters)
        ).then((res) => {
            const link = document.createElement("a");
            link.href = res.payload;
            link.setAttribute("download", "");
            document.body.appendChild(link);
            link.click();
            link.remove();
            setExportBtnDisabled(false);
        });
    };
    
    const exportInvoiceItemsAction = () => {
        if (exportBtnDisabled) return;
        setExportBtnDisabled(true);

        dispatch(
            exportInvoiceItems(filters)
        ).then((res) => {
            const link = document.createElement("a");
            link.href = res.payload;
            link.setAttribute("download", "");
            document.body.appendChild(link);
            link.click();
            link.remove();
            setExportBtnDisabled(false);
        });
    };

    return (
        <Fragment>
            <Breadcrumb title="Xero Invoices" data={dataMenu} />
            <Container fluid={true} className="finances-summary-page">
                {/* New Filters Card */}
                <Card className="px-3 mb-3">
                    <CardBody>
                        <Row>
                            <Col md="12">
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h6 className="mb-0">Filters</h6>
                                    <div>
                                        <ButtonDropdown
                                            color="success"
                                            isOpen={exportDropdownOpen}
                                            className={`float-right ml-2 ${
                                                exportBtnDisabled ? " disabled" : ""
                                            }`}
                                            toggle={exportToggle}
                                        >
                                            <DropdownToggle
                                                caret
                                                color="success"
                                                className="btn btn-success"
                                            >
                                                {exportBtnDisabled && (<i className="fa fa-spinner fa-spin fa-lg spinner mr-1" />)}
                                                Export
                                            </DropdownToggle>
                                            <DropdownMenu>
                                                <DropdownItem
                                                    onClick={() => { exportInvoicesAction(); }}
                                                >
                                                    Export Invoices
                                                </DropdownItem>
                                                <DropdownItem
                                                    onClick={() => { exportInvoiceItemsAction(); }}
                                                >
                                                    Export Invoice Items
                                                </DropdownItem>
                                            </DropdownMenu>
                                        </ButtonDropdown>
                                        <Button className="float-right" color="secondary" size="sm" onClick={clearFilters}>
                                            Clear Filters
                                        </Button>
                                    </div>
                                </div>
                            </Col>
                        </Row>

                        <Row>
                            <Col md="3">
                                <FormGroup>
                                    <Label>Date Range</Label>
                                    <DateRangePickerDefault
                                        dateRange={dateRange}
                                        dateRangeChange={(date) => {
                                            if (date === null) {
                                                setDateRange([null, null]);
                                            }else {
                                                setDateRange(date);
                                            }
                                        }}
                                        // defaultValue={(isValidDate(startDateObj) || isValidDate(endDateObj)) ? [startDateObj, endDateObj] : undefined}
                                        // disabledDate={afterToday()}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md="3">
                                <FormGroup>
                                    <Label for="filterInvoiceNumber">Invoice Number</Label>
                                    <Input
                                        type="text"
                                        id="filterInvoiceNumber"
                                        placeholder="Search by invoice number"
                                        value={filterInvoiceNumber}
                                        onChange={(e) => {
                                            handleTextInputChange('invoice_number', e.target.value);
                                            setFilterInvoiceNumber(e.target.value);
                                        }}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md="3">
                                <FormGroup>
                                    <Label for="filterContact">Contact</Label>
                                    <Input
                                        type="text"
                                        id="filterContact"
                                        placeholder="Search by contact name / email / phone number"
                                        value={filterContact}
                                        onChange={(e) => {
                                            handleTextInputChange('contact_detail', e.target.value);
                                            setFilterContact(e.target.value);
                                        }}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md="3">
                                <FormGroup>
                                    <Label for="currency">Currency</Label>
                                    <Select
                                        className="select2-filter"
                                        placeholder="Fillter by Currency"
                                        getOptionLabel={(option) => option.name}
                                        getOptionValue={(option) => option.id}
                                        value={filterCurrency}
                                        onChange={(e) => {
                                            handleFilterChange('currency', e ? e.id : null);
                                            setFilterCurrency(e);
                                        }}
                                        components={{
                                            IndicatorSeparator: () => null,
                                            DropdownIndicator: (props) => {
                                                return filterCurrency ? null : <components.DropdownIndicator {...props} />;
                                            }
                                        }}
                                        isClearable={true}
                                        options={xeroInvoiceState.settings.currencies}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>

                        <Row>
                            <Col md="">
                                <FormGroup>
                                    <Label for="type">Type</Label>
                                    <Select
                                        className="select2-filter"
                                        placeholder="Fillter by Type"
                                        getOptionLabel={(option) => option.name}
                                        getOptionValue={(option) => option.id}
                                        value={filterType}
                                        onChange={(e) => {
                                            handleFilterChange('type', e ? e.id : null);
                                            setFilterType(e);
                                        }}
                                        components={{
                                            IndicatorSeparator: () => null,
                                            DropdownIndicator: (props) => {
                                                return filterType ? null : <components.DropdownIndicator {...props} />;
                                            }
                                        }}
                                        isClearable={true}
                                        options={xeroInvoiceState.settings.types}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md="">
                                <FormGroup>
                                    <Label for="source_type">Source Type</Label>
                                    <Select
                                        className="select2-filter"
                                        placeholder="Fillter by Source Type"
                                        getOptionLabel={(option) => option.name}
                                        getOptionValue={(option) => option.id}
                                        value={filterSourceType}
                                        onChange={(e) => {
                                            handleFilterChange('source_type', e ? e.id : null);
                                            setFilterSourceType(e);
                                        }}
                                        components={{
                                            IndicatorSeparator: () => null,
                                            DropdownIndicator: (props) => {
                                                return filterSourceType ? null : <components.DropdownIndicator {...props} />;
                                            }
                                        }}
                                        isClearable={true}
                                        options={xeroInvoiceState.settings.source_types}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md="">
                                <FormGroup>
                                    <Label for="error_message">Error Status</Label>
                                    <Input
                                        type="select"
                                        id="error_message"
                                        value={filters.error_message}
                                        onChange={(e) => handleFilterChange('error_message', e.target.value)}
                                    >
                                        <option value="">All</option>
                                        <option value="yes">Has Error</option>
                                        <option value="no">No Error</option>
                                    </Input>
                                </FormGroup>
                            </Col>
                            <Col md="">
                                <FormGroup>
                                    <Label for="amountDiscrepancy">Amount Discrepancy</Label>
                                    <Input
                                        type="select"
                                        id="amountDiscrepancy"
                                        value={filters.amount_discrepancy}
                                        onChange={(e) => handleFilterChange('amount_discrepancy', e.target.value)}
                                    >
                                        <option value="">All</option>
                                        <option value="yes">Yes</option>
                                        <option value="no">No</option>
                                    </Input>
                                </FormGroup>
                            </Col>
                            <Col md="">
                                <FormGroup>
                                    <Label for="xero_status">Xero Status</Label>
                                    <Select
                                        className="select2-filter"
                                        placeholder="Fillter by status"
                                        getOptionLabel={(option) => option.name}
                                        getOptionValue={(option) => option.id}
                                        value={filterXeroStatus}
                                        onChange={(e) => {
                                            handleFilterChange('xero_status', e ? e.id : null);
                                            setFilterXeroStatus(e);
                                        }}
                                        components={{
                                            IndicatorSeparator: () => null,
                                            DropdownIndicator: (props) => {
                                                return filterXeroStatus ? null : <components.DropdownIndicator {...props} />;
                                            }
                                        }}
                                        isClearable={true}
                                        options={xeroInvoiceState.settings.xero_status}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                    </CardBody>
                </Card>

                <Card className="px-3">
                    <CardBody>
                        <BootstrapTable
                            headerColumns={tableColumns}
                            gridData={xeroInvoiceState.xeroInvoicesLists}
                            noAction
                            progressPending={loading}
                            paginationServer
                            paginationTotalRows={xeroInvoiceState.xeroInvoicesTableRow}
                            currentPage={filters.page}
                            perPage={filters.pageSize}
                            onChangePage={onPageChange}
                            onChangeRowsPerPage={onPageSizeChange}
                            size="md"
                        />
                    </CardBody>
                </Card>
            </Container>
        </Fragment>
    );
}

export default withReducer([{ report: reportReducer }])(XeroInvoice);